import { useEffect, useRef, useState } from 'react';
import type { Stone } from '../domain/types';
import { StoneIcon } from './StoneIcon';

interface StoneCarouselProps {
  stones: Stone[];
  /** 各石の成長段階(stonesと同じインデックス) */
  stages: number[];
  /** 0始まりの選択中インデックス */
  selectedIndex: number;
  onSelect: (index: number) => void;
  /** 所持していない石のときの代替描画(任意) */
  renderEmpty?: (index: number) => React.ReactNode;
}

const SLOT_OFFSET_X = 130; // 隣のスロットまでの水平距離
const SCALE_CENTER = 1.0;
const SCALE_SIDE = 0.65;
const SCALE_HIDDEN = 0.4;

/** タッチ+マウス両対応のスワイプ可能カルーセル。
 *  中央の石が選択状態、左右に前後の石をぼかし縮小で表示。 */
export function StoneCarousel({
  stones,
  stages,
  selectedIndex,
  onSelect,
  renderEmpty,
}: StoneCarouselProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startXRef = useRef<number | null>(null);
  const lastXRef = useRef<number | null>(null);

  // ドラッグ処理(タッチ+マウス両対応)
  useEffect(() => {
    const node = trackRef.current;
    if (!node) return;

    const handleStart = (clientX: number) => {
      startXRef.current = clientX;
      lastXRef.current = clientX;
      setIsDragging(true);
    };

    const handleMove = (clientX: number) => {
      if (startXRef.current == null) return;
      const dx = clientX - startXRef.current;
      setDragOffset(dx);
      lastXRef.current = clientX;
    };

    const handleEnd = () => {
      if (startXRef.current == null) return;
      const moved = dragOffset;
      const threshold = 50;
      if (moved > threshold && selectedIndex > 0) {
        onSelect(selectedIndex - 1);
      } else if (moved < -threshold && selectedIndex < stones.length - 1) {
        onSelect(selectedIndex + 1);
      }
      startXRef.current = null;
      lastXRef.current = null;
      setDragOffset(0);
      setIsDragging(false);
    };

    // Touch
    const onTouchStart = (e: TouchEvent) => handleStart(e.touches[0]!.clientX);
    const onTouchMove = (e: TouchEvent) => handleMove(e.touches[0]!.clientX);
    const onTouchEnd = () => handleEnd();

    // Mouse
    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      handleStart(e.clientX);
    };
    const onMouseMove = (e: MouseEvent) => {
      if (startXRef.current == null) return;
      handleMove(e.clientX);
    };
    const onMouseUp = () => handleEnd();
    const onMouseLeave = () => {
      if (startXRef.current != null) handleEnd();
    };

    node.addEventListener('touchstart', onTouchStart, { passive: true });
    node.addEventListener('touchmove', onTouchMove, { passive: true });
    node.addEventListener('touchend', onTouchEnd);
    node.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    node.addEventListener('mouseleave', onMouseLeave);

    return () => {
      node.removeEventListener('touchstart', onTouchStart);
      node.removeEventListener('touchmove', onTouchMove);
      node.removeEventListener('touchend', onTouchEnd);
      node.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      node.removeEventListener('mouseleave', onMouseLeave);
    };
  }, [dragOffset, selectedIndex, stones.length, onSelect]);

  const isEmpty = stones.length === 0;

  if (isEmpty) {
    return (
      <div className="carousel">
        {renderEmpty ? (
          renderEmpty(0)
        ) : (
          <div className="empty-state">
            <div className="empty-state__icon">✦</div>
            <div className="empty-state__msg">まだ石がありません</div>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="carousel">
        <div
          ref={trackRef}
          className={`carousel__track ${isDragging ? 'is-dragging' : 'is-animating'}`}
        >
          {stones.map((stone, idx) => {
            const offsetFromCenter = idx - selectedIndex;
            const abs = Math.abs(offsetFromCenter);
            const isCenter = abs === 0;
            const isSide = abs === 1;
            const isHidden = abs > 1;

            // ドラッグ中の追従
            const dragDelta = isDragging ? dragOffset / SLOT_OFFSET_X : 0;
            const visualOffset = offsetFromCenter - dragDelta;

            const translateX = visualOffset * SLOT_OFFSET_X;
            const scale = isCenter
              ? SCALE_CENTER
              : isSide
                ? SCALE_SIDE
                : SCALE_HIDDEN;
            const opacity = isCenter ? 1 : isSide ? 0.55 : 0;
            const zIndex = isCenter ? 3 : isSide ? 2 : 1;
            const slotClass = isCenter
              ? 'carousel__slot carousel__slot--center'
              : isSide
                ? 'carousel__slot carousel__slot--side'
                : 'carousel__slot carousel__slot--hidden';

            const stage = stages[idx] ?? 0;

            return (
              <div
                key={stone.id}
                className={slotClass}
                style={{
                  transform: `translate(calc(-50% + ${translateX}px), -50%) scale(${scale})`,
                  opacity,
                  zIndex,
                  filter: isSide ? 'blur(1.5px)' : 'none',
                }}
                onClick={() => {
                  if (isSide) onSelect(idx);
                }}
                role="button"
                tabIndex={isHidden ? -1 : 0}
                aria-hidden={isHidden}
                aria-label={`${stone.name}${isCenter ? '(選択中)' : ''}`}
              >
                <div className="carousel__slot-inner" style={{ color: stone.geometryParams.glowColor }}>
                  <div
                    className={`carousel__stone-wrap ${isCenter ? 'carousel__stone-wrap--center' : ''}`}
                  >
                    {isCenter && (
                      <>
                        <div className="carousel__aura" />
                        <svg
                          className="carousel__rays"
                          width="200"
                          height="200"
                          viewBox="0 0 200 200"
                          aria-hidden
                        >
                          {Array.from({ length: 12 }).map((_, i) => {
                            const angle = (i * 360) / 12;
                            return (
                              <line
                                key={i}
                                x1="100"
                                y1="100"
                                x2="100"
                                y2="20"
                                stroke={stone.geometryParams.glowColor}
                                strokeWidth="1.5"
                                opacity="0.5"
                                transform={`rotate(${angle} 100 100)`}
                              />
                            );
                          })}
                        </svg>
                      </>
                    )}
                    <StoneIcon stone={stone} stage={stage} size={isCenter ? 160 : 100} />
                  </div>
                  {isCenter && (
                    <>
                      <div className="carousel__stone-name">{stone.name}</div>
                      <div className="carousel__stone-stage">段階 {stage}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="carousel__hint">スワイプで石を選ぶ</div>

      <div className="carousel__dots">
        {stones.map((stone, idx) => (
          <button
            key={stone.id}
            className={`carousel__dot ${idx === selectedIndex ? 'is-active' : ''}`}
            onClick={() => onSelect(idx)}
            type="button"
            aria-label={`${stone.name}を選択`}
          />
        ))}
      </div>
    </>
  );
}
